import { useEffect, useRef } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import {
    EXPLORER_SESSION_TTL_MS,
    useExplorerStore,
} from '../../stores/explorerStore';

/** Persist activity at most this often while the user is interacting. */
export const EXPLORER_ACTIVITY_FLUSH_MS = 60 * 1000;

const ACTIVITY_EVENTS = ['pointerdown', 'keydown', 'scroll'] as const;

/**
 * Keep the Explorer session alive while the user is interacting, then expire
 * it one hour after the last activity — including while this tab stays open.
 */
export function useExplorerSessionActivity(): void {
    const { getDateFilterMinStartDate, getDateFilterMaxEndDate } = useAuth();
    const minDate = getDateFilterMinStartDate();
    const maxDate = getDateFilterMaxEndDate();
    const pendingRef = useRef(false);
    const lastFlushRef = useRef(Number.NEGATIVE_INFINITY);

    useEffect(() => {
        const flush = () => {
            if (!pendingRef.current) return;
            pendingRef.current = false;
            lastFlushRef.current = Date.now();
            useExplorerStore.getState().touchActivity();
        };

        const onActivity = () => {
            pendingRef.current = true;
            if (Date.now() - lastFlushRef.current >= EXPLORER_ACTIVITY_FLUSH_MS) {
                flush();
            }
        };

        const expireIfIdle = () => {
            const { savedAt, _hasRehydrated, initializeSessionDefaults } =
                useExplorerStore.getState();
            if (!_hasRehydrated || !savedAt) return;
            if (Date.now() - savedAt <= EXPLORER_SESSION_TTL_MS) return;
            initializeSessionDefaults(minDate, maxDate);
        };

        const onVisibility = () => {
            if (document.visibilityState === 'hidden') {
                flush();
                return;
            }
            expireIfIdle();
        };

        for (const event of ACTIVITY_EVENTS) {
            window.addEventListener(event, onActivity, { passive: true });
        }
        document.addEventListener('visibilitychange', onVisibility);
        window.addEventListener('pagehide', flush);

        const intervalId = window.setInterval(expireIfIdle, EXPLORER_ACTIVITY_FLUSH_MS);
        expireIfIdle();

        return () => {
            for (const event of ACTIVITY_EVENTS) {
                window.removeEventListener(event, onActivity);
            }
            document.removeEventListener('visibilitychange', onVisibility);
            window.removeEventListener('pagehide', flush);
            window.clearInterval(intervalId);
        };
    }, [minDate, maxDate]);
}
